import { useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import {
  Box,
  Chip,
  Divider,
  Grid,
  lighten,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { getShadedCommonground } from "../../utils/helpers";
import QuestionAcceptTermsDisplay from "./questionAcceptTermsDisplay";
import QuestionBloodPressueDisplay from "./questionBloodPressureDisplay";
import QuestionCaregiverDisplay from "./questionCaregiverDisplay";
import QuestionDateDisplay from "./questionDateDisplay";
import QuestionForBotpressDisplay from "./questionForBotpressDisplay";
import QuestionFreeTextDisplay from "./questionFreeTextDisplay";
import QuestionHeightDisplay from "./questionHeightDisplay";
import QuestionMultiImageSelectDisplay from "./questionMultiImageSelectDisplay";
import QuestionMultiSelectButtonDisplay from "./questionMultiSelectButtonDisplay";
import QuestionNumberScaleDisplay from "./questionNumberScaleDisplay";
import QuestionSingleImageSelectDisplay from "./questionSingleImageSelectDisplay";
import QuestionSingleSelectButtonDisplay from "./questionSingleSelectButtonDisplay";
import QuestionSliderScaleDisplay from "./questionSliderScaleDisplay";
import QuestionStopwatchDisplay from "./questionStopwatchDisplay";
import QuestionTextDisplay from "./questionTextDisplay";
import QuestionVideoPlayerDisplay from "./questionVideoPlayerDisplay";
import QuestionWeightDisplay from "./questionWeightDisplay";
import QuestionYearDisplay from "./questionYearDisplay";
import QuestionZipDisplay from "./questionZipDisplay";

//this has to behave like its own standalone component
const DisplayAnswerOptions = props => {
  const { question, onAnswerSelected, startNextTimer } = props;

  if (question.omniID === "ARPSIE33" || question.displayType === "zipCode") {
    //  zip code
    return (
      <QuestionZipDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.omniID === "ARPSIE24a" || question.displayType === "height") {
    //  Subject height
    return (
      <QuestionHeightDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.omniID === "ARPSIE24" || question.displayType === "weight") {
    // Subject weight
    return (
      <QuestionWeightDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.omniID === "ARPSIE10" || question.displayType === "bloodPressure") {
    // Blood Pressure
    return (
      <QuestionBloodPressueDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "button") {
    if (!question.multiSelectText || question.multiSelectText === "false") {
      //  single Button
      return (
        <QuestionSingleSelectButtonDisplay
          question={question}
          onAnswerSelected={onAnswerSelected}
          startNextTimer={startNextTimer}
        />
      );
    } else {
      //  Multi Select Buttons
      return (
        <QuestionMultiSelectButtonDisplay
          question={question}
          onAnswerSelected={onAnswerSelected}
          startNextTimer={startNextTimer}
        />
      );
    }
  } else if (question.displayType === "date") {
    return (
      <QuestionDateDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "year") {
    return (
      <QuestionYearDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "freeText") {
    return <QuestionFreeTextDisplay question={question} onAnswerSelected={onAnswerSelected} />;
  } else if (question.displayType === "text") {
    return <QuestionTextDisplay question={question} onAnswerSelected={onAnswerSelected} />;
  } else if (question.displayType === "caregiver") {
    return (
      <QuestionCaregiverDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "acceptTerms") {
    return (
      <QuestionAcceptTermsDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "image") {
    if (!question.multiSelectText || question.multiSelectText === "false") {
      //  single Button
      return (
        <QuestionSingleImageSelectDisplay
          question={question}
          onAnswerSelected={onAnswerSelected}
          startNextTimer={startNextTimer}
        />
      );
    } else {
      //  Multi Select Buttons
      return (
        <QuestionMultiImageSelectDisplay
          question={question}
          onAnswerSelected={onAnswerSelected}
          startNextTimer={startNextTimer}
        />
      );
    }
  } else if (question.displayType === "numberScale") {
    return (
      <QuestionNumberScaleDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "stopwatch") {
    return (
      <QuestionStopwatchDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "videoPlayer") {
    return (
      <QuestionVideoPlayerDisplay
        question={question}
        onAnswerSelected={onAnswerSelected}
        startNextTimer={startNextTimer}
      />
    );
  } else if (question.displayType === "sliderScale") {
    return <QuestionSliderScaleDisplay question={question} onAnswerSelected={onAnswerSelected} />;
  } else if (question.displayType === "questionForBotpress") {
    return <QuestionForBotpressDisplay question={question} onAnswerSelected={onAnswerSelected} />;
  } else {
    //default should be free text

    return <QuestionTextDisplay question={question} onAnswerSelected={onAnswerSelected} />;
  }
};

const getQuestionText = (numberText, text) => {
  return {
    __html: `<p style="margin:0; line-height: 1.5em;">${
      numberText != null ? numberText : ""
    } ${text}</p>`,
  };
};

//#endregion

const AssessmentQuestionDisplay = props => {
  const { questionODM, startNextTimer, setPendingAnswer } = props;

  //questionODM
  const theme = useTheme();

  const smallScreen = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const [showHelp, setShowHelp] = useState(false);

  return (
    <Grid
      id="assessmentQuestionDisplay2"
      item
      container
      direction="column"
      // spacing={4}
      sx={[
        {
          paddingBottom: theme.spacing(2),
          // width: "100% !important",
          marginLeft: "0px !important",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: theme.spacing(2),
            borderRadius: theme.spacing(1),
          },
          "&:hover::-webkit-scrollbar": {
            width: theme.spacing(2),
          },
          "&::-webkit-scrollbar-track": {
            borderRadius: theme.spacing(1),
            backgroundColor: getShadedCommonground(theme, 0.4),
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb": {
            opacity: "0.5",
            borderRadius: theme.spacing(1),
            backgroundColor: theme.palette.secondary.main,
          },
          "&:hover::-webkit-scrollbar-thumb": {
            backgroundColor: lighten(theme.palette.secondary.main, 0.2),
          },
        },
      ]}
    >
      <Grid
        id="assessmentQuestionDisplay3"
        container
        item
        direction="row"
        justifyContent="center"
        sx={{
          width: "100%",
          marginBottom: theme.spacing(1),
          paddingLeft: "0px !important",
          paddingRight: "0px !important",
        }}
      >
        <Grid
          id="assessmentQuestionDisplay44"
          container
          item
          justifyContent="space-between"
          xs={12}
        >
          <Grid
            item
            xs={12}
            // xs={questionODM.helpText && typeof questionODM.helpText !== "object" ? 11 : 12}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "16pt", textAlign: "justify" }}
              dangerouslySetInnerHTML={getQuestionText(null, questionODM.text)}
            />
          </Grid>
          {questionODM.helpText && questionODM.omniID !== "ARPSIE25" && (
            <>
              <Grid item>
                <Chip
                  size="small"
                  variant={"outlined"}
                  label={"Help"}
                  onClick={e => {
                    e.stopPropagation();
                    setShowHelp(showHelp => !showHelp);
                  }}
                  sx={[
                    {
                      position: "absolute",
                      right: "16px",
                      top: "68px",
                      borderColor: theme.palette.info.light,
                      color: theme.palette.info.light,
                      userSelect: "none",
                    },
                    showHelp && {
                      borderColor: theme.palette.success.light,
                      color: theme.palette.success.light,
                    },
                  ]}
                  icon={
                    showHelp ? (
                      <CheckIcon
                        fontSize="small"
                        sx={{
                          color: `${theme.palette.success.light} !important`,
                        }}
                      />
                    ) : null
                  }
                />
              </Grid>
              {showHelp && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      marginTop: theme.spacing(0.5),
                    }}
                  >
                    <HelpRoundedIcon
                      sx={{
                        color: theme.palette.success.light,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "14pt",
                        marginBottom: theme.spacing(1),
                        marginLeft: theme.spacing(0.5),
                      }}
                    >
                      {questionODM.helpText}
                    </Typography>
                  </Box>
                  <Divider />
                </Grid>
              )}
            </>
          )}
          {questionODM.helpText && questionODM.omniID === "ARPSIE25" && (
            <>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "14pt", marginBottom: theme.spacing(1) }}
                >
                  · {questionODM.helpText}
                </Typography>
                <Divider />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      <Grid
        id="assessmentQuestionDisplay4"
        item
        container
        xs={12}
        direction="row"
        sx={[
          !smallScreen && { minHeight: "260px" },
          questionODM.answerOptions.length < 1 && {
            display: "none",
          },
          { paddingTop: theme.spacing(2), paddingLeft: theme.spacing(1) },
        ]}
        justifyContent="stretch"
      >
        {questionODM.helpText && typeof questionODM.helpText === "object" && (
          <Grid container item xs={7} sx={{ marginTop: "-12px", marginBottom: "-12px" }}>
            <Grid container item xs={12} sx={{ marginLeft: theme.spacing(1), maxHeight: "24px" }}>
              <Grid item>
                <Typography variant="overline">{questionODM.subText}</Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  marginTop: theme.spacing(-1),
                  marginBottom: theme.spacing(1),
                  paddingRight: theme.spacing(2),
                }}
              >
                <Divider />
              </Grid>
              {questionODM.helpText.map((help, index) => {
                return (
                  <Grid
                    item
                    xs={12}
                    marginLeft={"8px"}
                    alignContent="flex-start"
                    sx={{ marginBottom: theme.spacing(1) }}
                  >
                    <Typography key={index} variant="body2">
                      · {Object.values(help)[0]}
                      {Object.values(help)[1] && (
                        <Typography variant="caption"> ({Object.values(help)[1]})</Typography>
                      )}
                    </Typography>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        )}
        <Grid
          container
          item
          direction="row"
          rowSpacing={3}
          justifyContent="center"
          alignSelf={
            questionODM.helpText && typeof questionODM.helpText === "object"
              ? "center"
              : "flex-start"
          }
          xs={questionODM.helpText && typeof questionODM.helpText === "object" ? 5 : 12}
        >
          {questionODM.answerOptions.length > 0 && (
            <DisplayAnswerOptions
              question={questionODM}
              onAnswerSelected={setPendingAnswer}
              startNextTimer={startNextTimer}
              smallScreen={smallScreen}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AssessmentQuestionDisplay;
